import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BookOpen } from 'lucide-react';

type Role = 'student' | 'parent' | 'teacher' | 'admin' | 'class_speaker';
const SELF_SERVE: Role[] = ['student', 'parent'];
const PRIVILEGED: Role[] = ['teacher', 'admin', 'class_speaker'];

const ROLE_LABEL: Record<Role, string> = {
  student: 'Schüler:in',
  parent: 'Elternteil',
  teacher: 'Lehrer:in',
  admin: 'Administrator:in',
  class_speaker: 'Klassensprecher:in',
};

const RegisterPage: React.FC = () => {
  const [params] = useSearchParams();
  const prefilledToken = params.get('invite_token') ?? '';
  const prefilledRole = (params.get('role') as Role) ?? 'student';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [klass, setKlass] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [role, setRole] = useState<Role>(prefilledRole);
  const [inviteToken, setInviteToken] = useState(prefilledToken);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<{ mailDelivered: boolean; email: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const needsInvite = useMemo(() => PRIVILEGED.includes(role), [role]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen haben.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }
    if (needsInvite && !inviteToken.trim()) {
      setError(`Für die Rolle "${ROLE_LABEL[role]}" ist ein Einladungs-Token erforderlich.`);
      return;
    }

    setIsLoading(true);
    try {
      const body: Record<string, string> = { email, password, name, role };
      if (klass) body.class = klass;
      if (inviteToken.trim()) body.invite_token = inviteToken.trim();

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Registrierung fehlgeschlagen.');
        return;
      }
      setSuccess({ mailDelivered: !!data.verification_email_delivered, email: data.email });
    } catch {
      setError('Netzwerkfehler. Bitte später erneut versuchen.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-gray-800 rounded-lg shadow-xl p-8 text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Fast geschafft!</h1>
          <p className="text-gray-300 mb-4">
            Wir haben eine Bestätigungs-E-Mail an <strong>{success.email}</strong> gesendet.
            Klicke den Link in der E-Mail, um dein Konto zu aktivieren.
          </p>
          {!success.mailDelivered && (
            <p className="text-yellow-400 text-sm mb-4">
              Hinweis: Der E-Mail-Versand ist fehlgeschlagen. Wende dich an den Administrator,
              damit dein Bestätigungs-Link manuell freigeschaltet wird.
            </p>
          )}
          <Link to="/login" className="inline-block mt-4 text-blue-400 hover:text-blue-300">
            Zurück zur Anmeldung
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <BookOpen className="w-10 h-10 text-blue-500" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Registrieren</h1>
          <p className="text-gray-400">Neues Hausaufgabenheft-Konto anlegen</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg shadow-xl p-8 space-y-5">
          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-400 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              placeholder="Vor- und Nachname"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Rolle</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
            >
              {SELF_SERVE.map((r) => (
                <option key={r} value={r}>{ROLE_LABEL[r]}</option>
              ))}
              {PRIVILEGED.map((r) => (
                <option key={r} value={r}>{ROLE_LABEL[r]} (Token nötig)</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Klasse {role === 'student' && <span className="text-gray-500">(optional)</span>}
            </label>
            <input
              type="text"
              value={klass}
              onChange={(e) => setKlass(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              placeholder="z. B. 10A"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">E-Mail</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              placeholder="deine@email.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Passwort</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              placeholder="mindestens 8 Zeichen"
              minLength={8}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Passwort bestätigen</label>
            <input
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              placeholder="Passwort wiederholen"
              minLength={8}
              required
            />
          </div>

          {needsInvite && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Einladungs-Token
              </label>
              <input
                type="text"
                value={inviteToken}
                onChange={(e) => setInviteToken(e.target.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 font-mono text-sm"
                placeholder="Token vom Admin"
                required
              />
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white font-semibold py-2 px-4 rounded-lg transition"
          >
            {isLoading ? 'Wird registriert...' : 'Konto erstellen'}
          </button>

          <p className="text-center text-sm text-gray-400">
            Schon registriert?{' '}
            <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium">
              Anmelden
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
